/**
 * La landing pública de cleanblue.cc.
 *
 * Es la única pantalla del sistema que cualquiera ve sin cuenta. Por eso no
 * lleva ni un dato real: nada de precios, nada de cuentas bancarias, nada de
 * un login de mentira. Los montos viven en el paquete contratado dentro del
 * portal; los datos de pago, dentro de la sesión de cada quien. Acá solo hay
 * contenido de mercadeo y enlaces al `/ingresar` real.
 *
 * `App.tsx` decide cuándo se muestra: solo a quien no tiene sesión.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { homeFor, useSession } from '@/lib/session';
import { Brandmark } from '@/components/common/Brandmark';

/** Mismo número que se muestra como teléfono/WhatsApp en el pie de página,
 *  en formato E.164 sin el "+" (lo que espera wa.me). Todos los CTA de la
 *  landing que antes solo desplazaban a #contacto ahora abren un chat de
 *  WhatsApp real — es el canal que la gente de verdad usa para escribirnos. */
const WHATSAPP_NUMBER = '50686376556';

/** Arma el link de wa.me. Un mensaje da contexto de qué botón lo trajo acá
 *  (agendar visita, elegir un paquete); sin mensaje, para los enlaces que ya
 *  dicen "WhatsApp" explícitamente, que solo necesitan abrir el chat. */
function waLink(message?: string) {
  const base = `https://wa.me/${WHATSAPP_NUMBER}`;
  return message ? `${base}?text=${encodeURIComponent(message)}` : base;
}

const WA_MSG_VISITA = 'Hola, quiero solicitar una visita para conocer el servicio de limpieza de Clean Blue.';
const WA_MSG_REGULAR = 'Hola, quiero contratar el Paquete Regular de limpieza de Clean Blue.';
const WA_MSG_PREMIUM = 'Hola, quiero contratar el Paquete Premium de limpieza de Clean Blue.';

/** Envuelve su contenido y lo revela al entrar en pantalla. Sin dependencias:
 *  un `IntersectionObserver` y una clase. Si el usuario pide menos movimiento,
 *  el CSS (`prefers-reduced-motion`) anula la animación y esto no hace nada. */
function Reveal({ children, className }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className={`landing-reveal${className ? ` ${className}` : ''}`}>
      {children}
    </div>
  );
}

/** Un ícono propio por servicio — nunca una tarjeta vacía. Vectorial, sin
 *  dependencias externas, con los mismos dos colores de marca que el resto
 *  del sitio (`--navy` y `--brand`), así que escala y se ve nítido a
 *  cualquier tamaño y en cualquier densidad de pantalla. */
const ICONO_LIMPIEZA_DIARIA = (
  <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <line x1="33" y1="8" x2="18" y2="27" stroke="var(--navy)" strokeWidth="3" strokeLinecap="round" />
    <polygon points="15,26 21,26 28,40 8,40" fill="var(--brand)" />
    <line x1="13" y1="31" x2="11" y2="40" stroke="var(--navy)" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="17" y1="30" x2="16" y2="40" stroke="var(--navy)" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="21" y1="30" x2="21" y2="40" stroke="var(--navy)" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="25" y1="30" x2="26" y2="40" stroke="var(--navy)" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M37 6 L38.3 9.2 L41.5 10.5 L38.3 11.8 L37 15 L35.7 11.8 L32.5 10.5 L35.7 9.2 Z" fill="var(--brand)" />
  </svg>
);
const ICONO_DESECHOS = (
  <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <rect x="19" y="7" width="10" height="4" rx="2" fill="var(--navy)" />
    <rect x="10" y="12" width="28" height="4" rx="2" fill="var(--navy)" />
    <path d="M13 18 L35 18 L32 40 Q32 42 30 42 L18 42 Q16 42 16 40 Z" fill="var(--brand)" />
    <line x1="19" y1="23" x2="20" y2="37" stroke="var(--navy)" strokeWidth="2" strokeLinecap="round" />
    <line x1="24" y1="23" x2="24" y2="37" stroke="var(--navy)" strokeWidth="2" strokeLinecap="round" />
    <line x1="29" y1="23" x2="28" y2="37" stroke="var(--navy)" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const ICONO_TRANSPORTE_EQUIPO = (
  <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <rect x="8" y="16" width="22" height="18" rx="2" fill="var(--brand)" />
    <path d="M8 16 L19 10 L30 16" fill="none" stroke="var(--navy)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="19" y1="16" x2="19" y2="34" stroke="var(--navy)" strokeWidth="2" />
    <circle cx="14" cy="38" r="3" fill="var(--navy)" />
    <circle cx="26" cy="38" r="3" fill="var(--navy)" />
    <path d="M34 20 L41 24 L34 28" fill="none" stroke="var(--navy)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ICONO_BIOINFECCIOSO = (
  <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <rect x="21" y="6" width="6" height="6" rx="3" fill="var(--navy)" />
    <rect x="22" y="12" width="4" height="4" fill="var(--navy)" />
    <path d="M14 16 Q14 14 16 14 L32 14 Q34 14 34 16 L36 38 Q36 42 32 42 L16 42 Q12 42 12 38 Z" fill="var(--navy)" />
    <rect x="21" y="22" width="6" height="14" rx="1" fill="var(--brand)" />
    <rect x="17" y="26" width="14" height="6" rx="1" fill="var(--brand)" />
  </svg>
);
const ICONO_LIMPIEZA_PROFUNDA = (
  <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="30" r="9" fill="none" stroke="var(--navy)" strokeWidth="2.5" />
    <circle cx="30" cy="24" r="6" fill="var(--brand)" />
    <circle cx="34" cy="36" r="4" fill="none" stroke="var(--navy)" strokeWidth="2" />
    <path d="M20 8 L22 13 L27 15 L22 17 L20 22 L18 17 L13 15 L18 13 Z" fill="var(--brand)" />
  </svg>
);
const ICONO_VENTANALES = (
  <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <rect x="8" y="8" width="32" height="32" rx="2" fill="none" stroke="var(--navy)" strokeWidth="2.5" />
    <line x1="24" y1="8" x2="24" y2="40" stroke="var(--navy)" strokeWidth="2.5" />
    <line x1="8" y1="24" x2="40" y2="24" stroke="var(--navy)" strokeWidth="2.5" />
    <line x1="14" y1="18" x2="20" y2="12" stroke="var(--brand)" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="18" y1="20" x2="24" y2="14" stroke="var(--brand)" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const SERVICIOS = [
  {
    title: 'Limpieza diaria de consultorios',
    hook: 'Superficies desinfectadas y pisos impecables, cada día.',
    icon: ICONO_LIMPIEZA_DIARIA,
  },
  {
    title: 'Retiro de desechos y reciclaje',
    hook: 'Residuos separados y retirados a diario, sin acumulación.',
    icon: ICONO_DESECHOS,
  },
  {
    title: 'Transporte al centro de equipo',
    hook: 'Instrumental trasladado y registrado, listo para esterilizar.',
    icon: ICONO_TRANSPORTE_EQUIPO,
  },
  {
    title: 'Transporte de material bioinfeccioso',
    hook: 'Residuos bioinfecciosos, etiquetados y trasladados con seguridad.',
    icon: ICONO_BIOINFECCIOSO,
  },
  {
    title: 'Limpieza profunda mensual',
    hook: 'Desinfección profunda mensual, con productos especializados.',
    icon: ICONO_LIMPIEZA_PROFUNDA,
  },
  {
    title: 'Limpieza de ventanales internos',
    hook: 'Ventanales y cristales, siempre transparentes.',
    icon: ICONO_VENTANALES,
  },
];

/** Íconos de los métodos de pago aceptados — genéricos, propios, sin ningún
 *  logo de tercero (SINPE / BAC son marcas registradas sin autorización de
 *  uso). Un teléfono para pago móvil, un banco para transferencia. */
const ICONO_PAGO_MOVIL = (
  <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <rect x="14" y="6" width="20" height="36" rx="4" fill="none" stroke="var(--navy)" strokeWidth="2.5" />
    <line x1="21" y1="10" x2="27" y2="10" stroke="var(--navy)" strokeWidth="2.5" strokeLinecap="round" />
    <rect x="17" y="15" width="14" height="18" rx="1" fill="var(--brand)" />
    <path d="M20 24 L23 27 L29 20" fill="none" stroke="var(--navy)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="24" cy="37.5" r="1.6" fill="var(--navy)" />
  </svg>
);
const ICONO_PAGO_TRANSFERENCIA = (
  <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 6 L42 16 L6 16 Z" fill="var(--navy)" />
    <rect x="6" y="38" width="36" height="4" rx="1" fill="var(--navy)" />
    <rect x="11" y="19" width="4" height="17" fill="var(--brand)" />
    <rect x="19" y="19" width="4" height="17" fill="var(--brand)" />
    <rect x="27" y="19" width="4" height="17" fill="var(--brand)" />
    <rect x="35" y="19" width="4" height="17" fill="var(--brand)" />
  </svg>
);

/** Íconos del bloque de contacto del pie: trazo simple en `currentColor`, no
 *  a dos tonos como los de arriba — ese estilo está pensado para fondo
 *  claro, y el pie es navy. El de WhatsApp es un globo de chat genérico, no
 *  el logo real (misma razón que SINPE/BAC arriba: es una marca de un
 *  tercero, y acá solo se nombra el canal, no se reproduce su ícono). */
const ICONO_TELEFONO = (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.4c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.4 0 .8-.2 1z"
      stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"
    />
  </svg>
);
const ICONO_CORREO = (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M4 6.5l8 6 8-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ICONO_WHATSAPP = (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 3a9 9 0 0 0-7.8 13.5L3 21l4.6-1.2A9 9 0 1 0 12 3z"
      stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"
    />
    <path d="M8.5 9.5c0 3.5 2.5 6 6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

function Nav() {
  const { user } = useSession();
  return (
    <div className="landing-nav">
      <div className="landing-nav__bar">
        <a href="#inicio" className="landing-nav__logo">
          <Brandmark size={34} />
          <span className="landing-nav__wordmark">CleanBlue</span>
        </a>
        <nav className="landing-nav__links">
          <a className="landing-nav__section-link" href="#servicios">Servicios</a>
          <a className="landing-nav__section-link" href="#paquetes">Paquetes</a>
          <a className="landing-nav__section-link" href="#contacto">Contacto</a>
          {/* Con sesión abierta pedir «Ingresar» no tiene sentido: ya ingresó.
              La landing es pública y visible siempre, así que acá se ofrece
              volver a lo suyo. */}
          {user
            ? <Link to={homeFor(user.role)} className="landing-nav__link">Ir a mi panel</Link>
            : <Link to="/ingresar" className="landing-nav__link">Ingresar</Link>}
          <a href={waLink(WA_MSG_VISITA)} target="_blank" rel="noopener noreferrer" className="btn btn--primary btn--sm">
            Solicitar visita
          </a>
        </nav>
      </div>
    </div>
  );
}

/** Hero — video real de fondo a todo lo ancho de la franja, con un panel de
 *  vidrio flotando encima para el texto. La línea de cobertura geográfica
 *  que vivía acá se reemplazó por la sección "Dónde estamos" (un mapa se ve,
 *  no se lee), así que no hay nada de ubicación en el hero mismo. */
function Hero() {
  return (
    <div id="inicio" className="landing-hero landing-hero--photo">
      <video className="landing-hero__bgimg" autoPlay muted loop playsInline src="/hero-bg.mp4" />
      <div className="landing-hero__scrim" />
      <div className="landing__wrap">
        <div className="landing-hero__panel">
          <span className="landing-eyebrow">Limpieza profesional</span>
          <h1>Limpieza con el rigor de un consultorio médico</h1>
          <p className="landing-lede">
            Protocolos de bioseguridad. Personal capacitado. Insumos controlados. Así cuidamos su
            consultorio, oficina o comercio.
          </p>
          <div className="landing-cta-row">
            <a href={waLink(WA_MSG_VISITA)} target="_blank" rel="noopener noreferrer" className="btn btn--primary btn--lg">
              Solicitar una visita
            </a>
            <a href="#paquetes" className="btn btn--ghost btn--lg">Ver paquetes</a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Servicios() {
  return (
    <div id="servicios" className="landing-section landing-section--tinted">
      <div className="landing__wrap">
        <Reveal className="landing-section__head-wrap">
          <div className="landing-section__head landing-section__head--split">
            <div>
              <span className="landing-eyebrow">Cómo trabajamos</span>
              <h2>Un servicio a la medida de cada espacio</h2>
            </div>
            <img
              className="landing-section__accent-img"
              src="/serv-persona.png"
              alt="Personal de Clean Blue durante un servicio de limpieza"
            />
          </div>
        </Reveal>
        <div className="landing-service-grid">
          {SERVICIOS.map((s) => (
            <Reveal key={s.title}>
              <div className="landing-card">
                <div className="landing-card__icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.hook}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Quiénes somos — mismo lenguaje que el hero: video de fondo, panel de
 *  vidrio flotando encima, esta vez anclado a la derecha para que el ritmo
 *  de la página no se sienta repetitivo bloque tras bloque. */
function QuienesSomos() {
  return (
    <div className="landing-section landing-quienes--photo">
      <video className="landing-hero__bgimg" autoPlay muted loop playsInline src="/video-quienes.mp4" />
      <div className="landing-hero__scrim" />
      <div className="landing__wrap landing-quienes__wrap">
        <Reveal className="landing-hero__panel landing-quienes__panel">
          <span className="landing-eyebrow">Quiénes somos</span>
          <h2>Especialistas en limpieza para entornos exigentes</h2>
          <p>
            Comenzamos limpiando consultorios médicos. Hoy llevamos ese mismo rigor a oficinas y
            comercios.
          </p>
          <ul className="landing-about__bullets">
            <li>Protocolos de bioseguridad</li>
            <li>Personal capacitado y verificado</li>
            <li>Insumos controlados</li>
          </ul>
        </Reveal>
      </div>
    </div>
  );
}

function Paquetes() {
  return (
    <div id="paquetes" className="landing-section landing-section--tinted">
      <div className="landing__wrap">
        <div className="landing-section__head landing-section__head--center">
          <span className="landing-eyebrow">Paquetes de servicio</span>
          <h2>Dos maneras de trabajar juntos</h2>
          <p className="muted">
            Cotización a la medida de su espacio — consultorio, oficina o comercio.
          </p>
        </div>
        <div className="landing-package-grid">
          <Reveal>
            <div className="landing-package">
              <div className="landing-package__head">
                <div>
                  <div className="landing-package__name">Paquete Regular</div>
                  <div className="landing-package__tag">Lo esencial del día a día, todos los días</div>
                </div>
              </div>
              <ul className="landing-package__list">
                <li className="landing-package__item">Limpieza diaria de consultorios</li>
                <li className="landing-package__item">Retiro de basura y reciclaje de materiales</li>
                <li className="landing-package__item">Transporte de material bioinfeccioso</li>
              </ul>
              <a
                href={waLink(WA_MSG_REGULAR)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn--ghost btn--block landing-package__cta"
              >
                Elegir Regular
              </a>
            </div>
          </Reveal>
          <Reveal>
            <div className="landing-package landing-package--featured">
              <div className="landing-package__head">
                <div>
                  <div className="landing-package__name">Paquete Premium</div>
                  <div className="landing-package__tag">Cobertura completa, hasta en emergencias</div>
                </div>
                {/* No usa el `Badge` del admin a propósito: ese componente marca
                    estados del sistema (activo, vencido) y por eso trae píldora
                    redonda y punto delante. Esto es una etiqueta de mercadeo, y
                    con ese disfraz leía como chip de app genérica. */}
                <span className="landing-package__badge">Recomendado</span>
              </div>
              <ul className="landing-package__list">
                <li className="landing-package__list-title">Todo lo del Paquete Regular, más:</li>
                <li className="landing-package__item">Limpieza profunda (mensual)</li>
                <li className="landing-package__item">Transporte de materiales al centro de equipo</li>
                <li className="landing-package__item">Limpieza de ventanales del área interna</li>
                <li className="landing-package__item">Limpieza por emergencias en horario de atención</li>
              </ul>
              <a
                href={waLink(WA_MSG_PREMIUM)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn--primary btn--block landing-package__cta"
              >
                Elegir Premium
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}

/** Métodos de pago: la sección existe para generar confianza, no para mostrar
 *  ningún dato real. Nunca un número de cuenta, ni siquiera enmascarado, ni
 *  el logo de SINPE o del banco — son marcas de terceros sin autorización de
 *  uso. Solo íconos propios y el nombre del método; el dato exacto vive
 *  dentro del portal de cada cliente, y la única acción es el `/ingresar`
 *  real — nunca un botón que simule sesión. */
function MetodosDePago() {
  return (
    <div className="landing-section">
      <div className="landing__wrap landing-payment__grid">
        <Reveal className="landing-payment__copy">
          <span className="landing-eyebrow">Portal de clientes</span>
          <h2>Su portal de cliente, siempre disponible</h2>
          <p>
            Revise facturas y pague por SINPE Móvil o transferencia bancaria, todo desde un mismo
            lugar.
          </p>
          <div className="landing-payment__card">
            <div className="landing-payment__card-head">
              <span>Aceptamos</span>
            </div>
            <div className="landing-payment__pills">
              <span className="landing-payment__pill">
                <span className="landing-payment__pill-icon">{ICONO_PAGO_MOVIL}</span>
                SINPE Móvil
              </span>
              <span className="landing-payment__pill">
                <span className="landing-payment__pill-icon">{ICONO_PAGO_TRANSFERENCIA}</span>
                Transferencia bancaria (BAC)
              </span>
            </div>
          </div>
          {/* El enlace de admin queda oculto hasta hover/focus de la fila: es
              una salida secundaria para un caso minoritario, no debe competir
              visualmente con el CTA principal del portal. */}
          <div className="landing-cta-row landing-cta-row--hover-admin">
            <Link to="/ingresar" className="btn btn--primary">Ingresar al portal</Link>
            <Link to="/ingresar" className="landing-payment__admin-link">¿Administrador? Ingresar al panel →</Link>
          </div>
        </Reveal>
        <img
          className="landing-payment__img"
          src="/serv-ventana.png"
          alt="Personal de Clean Blue limpiando un ventanal"
        />
      </div>
    </div>
  );
}

/** Datos del heightmap servido como JSON estático (`/holo-heightmap.json`):
 *  una grilla de `gw`×`gh` celdas de elevación (0 = fuera del país, 1-255 =
 *  altura), codificada en base64 para no repetir separadores por celda. Se
 *  muestreó una sola vez del mapa de relieve físico real de Costa Rica —
 *  ver la sesión de trabajo del landing para el detalle de esa extracción. */
type HoloMeta = { gw: number; gh: number; aspect: number; b64: string };
type HoloPoint = { x: number; y: number; z: number; e: number; gx: number; gy: number; dSJ: number };
type HoloBeacon = { pt: HoloPoint; labelEl: HTMLElement | null; colH: number; phase: number };

/** El holograma de "Dónde estamos" no es una imagen: es una malla de ~10k
 *  puntos con la elevación real de Costa Rica, proyectada en 3D a mano
 *  (sin librería de render) en cada cuadro de un `<canvas>`. Vive en un
 *  hook aparte porque es imperativo de punta a punta — no hay estado de
 *  React que mostrar, solo un `canvas` que se dibuja solo. */
function useHolograma(
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  sceneRef: React.RefObject<HTMLDivElement | null>,
  label1Ref: React.RefObject<HTMLSpanElement | null>,
  label2Ref: React.RefObject<HTMLSpanElement | null>,
) {
  useEffect(() => {
    const canvasMaybe = canvasRef.current;
    const scene = sceneRef.current;
    if (!canvasMaybe || !canvasMaybe.getContext) return undefined;
    const ctxMaybe = canvasMaybe.getContext('2d');
    if (!ctxMaybe) return undefined;
    // Re-vincular con un tipo no-nulo: la angosta narrowing de flujo de TS
    // no siempre se conserva dentro de funciones anidadas definidas más
    // abajo (`resize`, `project`, `frame`...) — esto lo garantiza a nivel
    // de tipo en vez de depender de esa inferencia.
    const canvas: HTMLCanvasElement = canvasMaybe;
    const ctx: CanvasRenderingContext2D = ctxMaybe;

    let cancelled = false;
    let raf = 0;
    let cleanup: (() => void) | undefined;

    fetch('/holo-heightmap.json')
      .then((r) => r.json() as Promise<HoloMeta>)
      .then((META) => {
        if (cancelled) return;

        const bin = atob(META.b64);
        const GW = META.gw;
        const GH = META.gh;
        const ASPECT = META.aspect;
        const elev = new Float32Array(GW * GH);
        for (let i = 0; i < bin.length; i++) elev[i] = bin.charCodeAt(i) / 255;

        // El centro de la vista va corrido hacia Curridabat: zoom cercano a
        // nuestra zona, los bordes del país se salen del encuadre — es lo
        // buscado (se probó el país completo y quedó la versión cercana).
        const SJ = { x: 0.565, y: 0.410 };
        const CENTER = { x: 0.5 + (SJ.x - 0.5) * 0.6, y: 0.5 + (SJ.y - 0.5) * 0.6 };
        const EXAG = 0.075; // altura máxima como fracción del ancho del país — relieve real, no exagerado
        const pts: HoloPoint[] = [];
        for (let gy = 0; gy < GH; gy++) {
          for (let gx = 0; gx < GW; gx++) {
            const v = elev[gy * GW + gx];
            if (!v) continue;
            pts.push({
              x: gx / GW - CENTER.x,
              y: (gy / GH - CENTER.y) * ASPECT,
              z: Math.pow(v, 1.05) * EXAG,
              e: v,
              gx,
              gy,
              dSJ: Math.hypot(gx / GW - SJ.x, (gy / GH - SJ.y) * ASPECT),
            });
          }
        }
        const index = new Map<number, HoloPoint>();
        pts.forEach((p) => index.set(p.gy * GW + p.gx, p));

        // El heightmap siempre trae al menos una celda dentro del país, así
        // que `pts` nunca está vacío en la práctica — el `!` final encierra
        // esa garantía sin arrastrar `| undefined` por el resto del código.
        function nearest(tx: number, ty: number): HoloPoint {
          let found: HoloPoint | undefined;
          let best = Infinity;
          pts.forEach((p) => {
            const d = Math.hypot(p.gx / GW - tx, (p.gy / GH - ty) * ASPECT);
            if (d < best) { best = d; found = p; }
          });
          return found ?? pts[0]!;
        }

        // Dos ubicaciones en Curridabat: Clínica Bíblica y Aleste
        // Corporativo. Están a ~1km una de otra — a esta escala serían el
        // mismo píxel, así que Aleste va con una separación levemente
        // exagerada (licencia cartográfica) para que los dos pins se lean.
        // Tupla de largo fijo (no `HoloBeacon[]`): así `BEACONS[0]`/`[1]`
        // quedan tipados sin `| undefined` más abajo.
        const BEACONS: [HoloBeacon, HoloBeacon] = [
          { pt: nearest(SJ.x, SJ.y), labelEl: label1Ref.current, colH: 0.14, phase: 0 },
          { pt: nearest(0.588, 0.422), labelEl: label2Ref.current, colH: 0.10, phase: 1.1 },
        ];

        const TILT = 0.96; // ~55°: angulado, no cenital — el ángulo aprobado
        const cosT = Math.cos(TILT);
        const sinT = Math.sin(TILT);
        const ZOOM = 1.5; // vista cercana sobre el Valle Central — la aprobada
        const yawBase = -0.22;
        let yawMouse = 0;
        let yawMouseTarget = 0;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        let W = 0;
        let H = 0;
        let dpr = 1;
        // Factor del `zoom` que la landing aplica en pantallas grandes (ver
        // "Zoom por tamano de pantalla" en app.css). 1 cuando no hay zoom.
        let zoomScale = 1;
        function resize() {
          dpr = window.devicePixelRatio || 1;
          const r = canvas.getBoundingClientRect();
          // W/H van en px LOCALES (`offsetWidth`), no pintados
          // (`getBoundingClientRect`). Es el mismo sistema de coordenadas en
          // el que se posicionan las etiquetas de los beacons mas abajo, con
          // `style.left/top`. Cuando esto usaba los px pintados, con el zoom
          // activo la proyeccion vivia en un espacio de 2545px mientras las
          // etiquetas se colocaban en uno de 1515: quedaban corridas por el
          // factor del zoom, encima del panel de texto.
          W = canvas.offsetWidth;
          H = canvas.offsetHeight;
          // El buffer si va en px pintados, o el holograma se veria borroso;
          // el `setTransform` de `frame()` traduce de locales a pintados.
          zoomScale = W > 0 ? r.width / W : 1;
          canvas.width = Math.round(W * dpr * zoomScale);
          canvas.height = Math.round(H * dpr * zoomScale);
        }
        resize();
        // Redibuja tras redimensionar: asignar `canvas.width` BORRA el canvas,
        // y con `prefers-reduced-motion` no hay bucle de animacion que lo
        // vuelva a pintar — el holograma quedaba en blanco para siempre.
        window.addEventListener('resize', () => { resize(); frame(0); });

        function project(p: { x: number; y: number; z: number }, yaw: number, bob: number) {
          const cy = Math.cos(yaw);
          const sy = Math.sin(yaw);
          const x1 = p.x * cy - p.y * sy;
          const y1 = p.x * sy + p.y * cy;
          const yv = y1 * cosT - p.z * sinT;
          const depth = y1 * sinT + p.z * cosT;
          const persp = 1 / (1 + depth * 0.55);
          // El canvas cubre la sección entera (panorámico): la escala se
          // ancla a la altura, y el país se corre a la izquierda en
          // pantallas anchas para que el panel de texto no lo tape.
          const s = Math.min(W, H * 1.5) * ZOOM;
          const cxScreen = W > 820 ? W * 0.40 : W / 2;
          return {
            sx: cxScreen + x1 * s * persp,
            sy: H * 0.52 + yv * s * persp + bob,
            persp,
          };
        }

        function colorFor(p: HoloPoint): [number, number, number] {
          // dim navy → brand → cian encendido según elevación; cerca de
          // Curridabat se enciende hacia celeste casi blanco (mismo tono,
          // más luz — nada de colores ajenos a la marca).
          const e = p.e;
          let r: number;
          let g: number;
          let b: number;
          if (e < 0.5) {
            const k = e / 0.5;
            r = 28 + (72 - 28) * k; g = 74 + (144 - 74) * k; b = 110 + (186 - 110) * k;
          } else {
            const k2 = (e - 0.5) / 0.5;
            r = 72 + (168 - 72) * k2; g = 144 + (228 - 144) * k2; b = 186 + (255 - 186) * k2;
          }
          const wSJ = Math.max(0, 1 - p.dSJ / 0.16);
          if (wSJ > 0) {
            r += (222 - r) * wSJ * 0.9; g += (242 - g) * wSJ * 0.9; b += (255 - b) * wSJ * 0.9;
          }
          return [Math.round(r), Math.round(g), Math.round(b)];
        }

        function frame(tMs: number) {
          const t = tMs / 1000;
          const k = dpr * zoomScale;
          ctx.setTransform(k, 0, 0, k, 0, 0);
          ctx.clearRect(0, 0, W, H);

          yawMouse += (yawMouseTarget - yawMouse) * 0.06;
          const yaw = reduceMotion ? yawBase : yawBase + Math.sin(t * 0.22) * 0.16 + yawMouse;
          const bob = reduceMotion ? 0 : Math.sin(t * 0.8) * 3.5;

          // Resplandor suave bajo el país — sin anillos ni radar, solo un
          // piso de luz que ancla el holograma.
          const N = 64;
          const g0 = project({ x: 0, y: 0, z: 0 }, yaw, bob);
          const grad = ctx.createRadialGradient(g0.sx, g0.sy, 0, g0.sx, g0.sy, W * 0.44);
          grad.addColorStop(0, 'rgba(72,144,186,.16)');
          grad.addColorStop(1, 'rgba(72,144,186,0)');
          ctx.fillStyle = grad;
          ctx.fillRect(0, 0, W, H);

          // Malla: líneas cada 4 celdas, tenues.
          ctx.lineWidth = 0.6;
          ctx.strokeStyle = 'rgba(110,190,235,.13)';
          ctx.beginPath();
          for (let gy2 = 0; gy2 < GH; gy2 += 4) {
            let started = false;
            for (let gx2 = 0; gx2 < GW; gx2++) {
              const p2 = index.get(gy2 * GW + gx2);
              if (!p2) { started = false; continue; }
              const q2 = project(p2, yaw, bob);
              if (!started) { ctx.moveTo(q2.sx, q2.sy); started = true; } else ctx.lineTo(q2.sx, q2.sy);
            }
          }
          for (let gx3 = 0; gx3 < GW; gx3 += 4) {
            let started2 = false;
            for (let gy3 = 0; gy3 < GH; gy3++) {
              const p3 = index.get(gy3 * GW + gx3);
              if (!p3) { started2 = false; continue; }
              const q3 = project(p3, yaw, bob);
              if (!started2) { ctx.moveTo(q3.sx, q3.sy); started2 = true; } else ctx.lineTo(q3.sx, q3.sy);
            }
          }
          ctx.stroke();

          // Puntos.
          for (let pi = 0; pi < pts.length; pi++) {
            const p = pts[pi];
            if (!p) continue;
            const q = project(p, yaw, bob);
            if (q.sx < -8 || q.sx > W + 8 || q.sy < -8 || q.sy > H + 8) continue;
            const c = colorFor(p);
            const rad2 = (0.9 + p.e * 1.7) * q.persp * (W / 420);
            const alpha = 0.5 + p.e * 0.45;
            ctx.fillStyle = `rgba(${c[0]},${c[1]},${c[2]},${Math.min(1, alpha)})`;
            ctx.beginPath();
            ctx.arc(q.sx, q.sy, rad2, 0, 6.2832);
            ctx.fill();
          }

          // Beacons: columna de luz + halo + anillo pulsante, en celeste de marca.
          for (const bc of BEACONS) {
            const qb = project(bc.pt, yaw, bob);
            const qt = project({ x: bc.pt.x, y: bc.pt.y, z: bc.pt.z + bc.colH }, yaw, bob);
            const lg = ctx.createLinearGradient(qb.sx, qb.sy, qt.sx, qt.sy);
            lg.addColorStop(0, 'rgba(168,228,255,.95)');
            lg.addColorStop(1, 'rgba(168,228,255,0)');
            ctx.strokeStyle = lg;
            ctx.lineWidth = 2.4;
            ctx.beginPath(); ctx.moveTo(qb.sx, qb.sy); ctx.lineTo(qt.sx, qt.sy); ctx.stroke();
            const hg = ctx.createRadialGradient(qb.sx, qb.sy, 0, qb.sx, qb.sy, 24);
            hg.addColorStop(0, 'rgba(168,228,255,.8)');
            hg.addColorStop(1, 'rgba(168,228,255,0)');
            ctx.fillStyle = hg;
            ctx.beginPath(); ctx.arc(qb.sx, qb.sy, 24, 0, 6.2832); ctx.fill();
            ctx.fillStyle = '#a8e4ff';
            ctx.beginPath(); ctx.arc(qb.sx, qb.sy, 3.2, 0, 6.2832); ctx.fill();
            if (!reduceMotion) {
              const pulse = ((t + bc.phase) % 2.2) / 2.2;
              const prad = 0.03 + pulse * 0.1;
              ctx.beginPath();
              for (let a2 = 0; a2 <= N; a2++) {
                const ang2 = (a2 / N) * Math.PI * 2;
                const qp = project(
                  { x: bc.pt.x + Math.cos(ang2) * prad, y: bc.pt.y + Math.sin(ang2) * prad, z: bc.pt.z },
                  yaw,
                  bob,
                );
                if (a2 === 0) ctx.moveTo(qp.sx, qp.sy); else ctx.lineTo(qp.sx, qp.sy);
              }
              ctx.strokeStyle = `rgba(168,228,255,${0.85 * (1 - pulse)})`;
              ctx.lineWidth = 2;
              ctx.stroke();
            }
            if (bc.labelEl) {
              bc.labelEl.style.left = `${qt.sx}px`;
              bc.labelEl.style.top = `${qt.sy}px`;
            }
          }

          // "CURRIDABAT" impreso sobre el terreno en oscuro, como rótulo de
          // mapa — proyectado en el plano del país (se mueve con él), no un
          // elemento flotante del DOM.
          //
          // En pantalla angosta no se dibuja: el cuerpo es `10.5 * (W / 900)`,
          // así que por debajo de 860px cae bajo los 10px y en un teléfono
          // queda en 4,5px — ilegible. Pintarlo en celeste de holograma no lo
          // salvaría (sería ruido brillante en vez de ruido oscuro) y encima
          // competiría con las etiquetas de los dos beacons, que son la
          // información que ahí importa. El umbral es el mismo breakpoint que
          // ya reordena esta sección en el CSS.
          if (W >= 860) {
            const p1 = BEACONS[0].pt;
            const p2 = BEACONS[1].pt;
            const gm = project(
              { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 + 0.055, z: Math.max(p1.z, p2.z) * 0.9 },
              yaw,
              bob,
            );
            ctx.save();
            ctx.font = `700 ${10.5 * (W / 900)}px -apple-system, 'Segoe UI', Roboto, sans-serif`;
            ctx.textAlign = 'center';
            const ctxWithLetterSpacing = ctx as CanvasRenderingContext2D & { letterSpacing?: string };
            if ('letterSpacing' in ctxWithLetterSpacing) ctxWithLetterSpacing.letterSpacing = '3px';
            ctx.fillStyle = 'rgba(16,20,34,.78)';
            ctx.fillText('CURRIDABAT', gm.sx, gm.sy);
            ctx.restore();
          }
        }

        frame(0); // primer cuadro garantizado aunque no corra la animación
        function loop(tMs: number) {
          frame(tMs);
          raf = requestAnimationFrame(loop);
        }
        if (!reduceMotion) raf = requestAnimationFrame(loop);

        function handleMouseMove(e: MouseEvent) {
          if (!scene) return;
          const r = scene.getBoundingClientRect();
          yawMouseTarget = ((e.clientX - r.left) / r.width - 0.5) * 0.4;
        }
        function handleMouseLeave() {
          yawMouseTarget = 0;
        }
        if (scene && !reduceMotion) {
          scene.addEventListener('mousemove', handleMouseMove);
          scene.addEventListener('mouseleave', handleMouseLeave);
        }

        cleanup = () => {
          window.removeEventListener('resize', resize);
          if (scene) {
            scene.removeEventListener('mousemove', handleMouseMove);
            scene.removeEventListener('mouseleave', handleMouseLeave);
          }
        };
      })
      .catch(() => {
        // Sin el heightmap (offline, bloqueo de red) la sección queda sin
        // holograma — no debe romper el resto de la landing.
      });

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      if (cleanup) cleanup();
    };
  }, [canvasRef, sceneRef, label1Ref, label2Ref]);
}

/** Dónde estamos: el holograma cubre toda la sección (la "filmina"
 *  completa), con el texto en un panel de vidrio oscuro flotando encima.
 *  Reemplaza la vieja línea de cobertura del hero — un mapa se ve, no se
 *  lee — y ahora marca las dos direcciones reales en Curridabat: Clínica
 *  Bíblica y Aleste Corporativo. */
function Mapa() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<HTMLDivElement>(null);
  const label1Ref = useRef<HTMLSpanElement>(null);
  const label2Ref = useRef<HTMLSpanElement>(null);
  useHolograma(canvasRef, sceneRef, label1Ref, label2Ref);

  return (
    <div id="mapa" className="landing-mapa--holo">
      <div className="landing-holo__scene" ref={sceneRef}>
        <canvas
          className="landing-holo__canvas"
          ref={canvasRef}
          role="img"
          aria-label="Holograma 3D del relieve de Costa Rica con nuestra ubicación en Curridabat"
        />
        <span className="landing-holo__beacon-label" ref={label1Ref}>
          <img className="landing-holo__beacon-iso" src="/biblica-isotipo.svg" alt="" />
          Clínica Bíblica
        </span>
        <span className="landing-holo__beacon-label" ref={label2Ref}>
          <img className="landing-holo__beacon-iso landing-holo__beacon-iso--aleste" src="/aleste-arrow.svg" alt="" />
          Aleste Corporativo
        </span>
      </div>
      <div className="landing__wrap">
        <div className="landing-holo__copy">
          <span className="landing-eyebrow">Dónde estamos</span>
          <h2>Nuestra presencia, marcada en el mapa</h2>
          <p>
            Atendemos consultorios médicos, oficinas y comercios en todo Costa Rica, con el
            cuidado que un espacio de salud exige.
          </p>
          <div className="landing-holo__logos">
            <img className="landing-holo__logos-biblica" src="/biblica-logo.svg" alt="Clínica Bíblica" />
            <img className="landing-holo__logos-aleste" src="/aleste-logo.svg" alt="Aleste Corporativo" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Contacto() {
  return (
    <div id="contacto" className="landing-footer">
      {/* Dos franjas, no una sola: el CTA (mismo navy de siempre) y la
          navegación del pie en un tono más oscuro debajo, para que el
          contacto —lo que de verdad importa acá— no se diluya entre los
          enlaces secundarios. */}
      <div className="landing-footer__cta">
        <Reveal className="landing-footer__top">
          <div>
            <h2>Coordinemos una visita a su consultorio</h2>
            <p>
              Escríbanos y le ayudamos a encontrar el paquete que mejor se ajusta a su espacio.
            </p>
          </div>
          {/* Solo dos canales, mismo tamaño de letra, sin jerarquía visual
              entre ellos: WhatsApp primero (el que se prefiere) y correo al
              final (el que menos). El teléfono suelto se quitó — mostraba el
              mismo número que ya está en la fila de WhatsApp, un link directo
              de por sí, así que era redundante. */}
          {/* El ícono de WhatsApp ya dice qué hace ese link — repetir el
              número al lado era decir lo mismo dos veces. El número, aparte,
              es su propia fila y hace lo suyo (llamar); cada uno con su
              ícono, para que no se confundan entre sí a simple vista. */}
          <div className="landing-footer__contact">
            <a className="landing-footer__contact-item" href={waLink()} target="_blank" rel="noopener noreferrer">
              <span className="landing-footer__contact-ico">{ICONO_WHATSAPP}</span>
              WhatsApp
            </a>
            <a className="landing-footer__contact-item" href="tel:86376556">
              <span className="landing-footer__contact-ico">{ICONO_TELEFONO}</span>
              8637-6556
            </a>
            <a className="landing-footer__contact-item" href="mailto:contacto@cleanblue.com">
              <span className="landing-footer__contact-ico">{ICONO_CORREO}</span>
              contacto@cleanblue.com
            </a>
          </div>
        </Reveal>
      </div>

      <div className="landing-footer__nav">
        <div className="landing-footer__nav-inner">
          {/* Mismas rutas que ya existen en el resto del sitio (nav, el botón
              de "Ingresar al portal" de arriba, Privacidad/Términos) — acá
              solo se reordenan en columnas, no se inventa ningún destino
              nuevo. */}
          <div className="landing-footer__cols">
            <div className="landing-footer__col landing-footer__col--brand">
              <a href="#inicio" className="landing-footer__logo">
                <Brandmark size={28} />
                <span className="landing-footer__brand">CleanBlue</span>
              </a>
              <span className="landing-footer__tag">Limpieza profesional · Costa Rica</span>
            </div>
            <div className="landing-footer__col">
              <span className="landing-footer__col-label">Sitio</span>
              <a href="#servicios">Servicios</a>
              <a href="#paquetes">Paquetes</a>
              <a href="#contacto">Contacto</a>
            </div>
            <div className="landing-footer__col">
              <span className="landing-footer__col-label">Cuenta</span>
              <Link to="/ingresar">Ingresar al portal</Link>
              <Link to="/ingresar">Ingresar al panel administrativo</Link>
            </div>
            <div className="landing-footer__col">
              <span className="landing-footer__col-label">Legal</span>
              <Link to="/privacidad">Privacidad</Link>
              <Link to="/terminos">Términos</Link>
            </div>
          </div>

          <div className="landing-footer__copyright">
            © {new Date().getFullYear()} Clean Blue. Todos los derechos reservados.
          </div>
        </div>
      </div>
    </div>
  );
}

/** La landing pública completa. Sin lógica de negocio, sin datos de sesión:
 *  es una capa de mercadeo estática sobre contenido fijo. */
export function PublicLanding() {
  return (
    <div className="landing">
      <Nav />
      <Hero />
      <Servicios />
      <QuienesSomos />
      <Paquetes />
      <MetodosDePago />
      <Mapa />
      <Contacto />
    </div>
  );
}
